<template>
  <button
    type="button"
    class="nav-chat-color-theme-toggle"
    :aria-label="toggleAriaLabel"
    :aria-pressed="isLightThemeActive"
    @click="onToggleChatColorTheme">
    <!-- Mode sombre actif → soleil doré -->
    <svg
      v-if="!isLightThemeActive"
      class="nav-chat-color-theme-toggle__icon nav-chat-color-theme-toggle__icon--sun"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="4.25"
        fill="currentColor" />
      <path
        d="M12 2.25v2.5M12 19.25v2.5M4.22 4.22l1.77 1.77M18.01 18.01l1.77 1.77M2.25 12h2.5M19.25 12h2.5M4.22 19.78l1.77-1.77M18.01 5.99l1.77-1.77"
        stroke="currentColor"
        stroke-width="1.75"
        stroke-linecap="round" />
    </svg>
    <!-- Mode clair actif → lune violette (même poids visuel que le soleil) -->
    <svg
      v-else
      class="nav-chat-color-theme-toggle__icon nav-chat-color-theme-toggle__icon--moon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true">
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
        fill="currentColor" />
      <circle
        cx="18.25"
        cy="5.75"
        r="0.9"
        fill="currentColor"
        opacity="0.9" />
      <circle
        cx="20.25"
        cy="8.75"
        r="0.55"
        fill="currentColor"
        opacity="0.65" />
    </svg>
  </button>
</template>

<script lang="ts" setup>
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import { chatColorThemePreference } from '~/utils/ChatColorThemePreference'

const { t } = useI18n()
const { track } = useTracking()

const chatColorTheme = chatColorThemePreference.activeTheme

const isLightThemeActive: ComputedRef<boolean> = computed(
  (): boolean => chatColorTheme.value === 'light',
)

const toggleAriaLabel: ComputedRef<string> = computed((): string => {
  return isLightThemeActive.value
    ? t('chat.colorTheme.switchToDark')
    : t('chat.colorTheme.switchToLight')
})

function onToggleChatColorTheme(): void {
  const nextTheme = chatColorThemePreference.toggleTheme()
  track(TRACKING_EVENTS.CHAT_THEME_CHANGED, {
    theme: nextTheme,
    source: 'toggle',
  })
}
</script>

<style scoped>
.nav-chat-color-theme-toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border-subtle);
  background: var(--chat-nav-theme-toggle-bg, rgba(255, 255, 255, 0.05));
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}

.nav-chat-color-theme-toggle:hover {
  border-color: var(--border-default);
  background: var(--chat-nav-theme-toggle-bg-hover, rgba(255, 255, 255, 0.08));
  transform: scale(1.04);
}

.nav-chat-color-theme-toggle__icon {
  display: block;
}

.nav-chat-color-theme-toggle__icon--sun {
  color: var(--accent-orange-lt);
}

.nav-chat-color-theme-toggle__icon--moon {
  color: var(--chat-nav-theme-toggle-moon-color, #8b5cf6);
}
</style>
